import matter from '../components/matter.min';
import { SeededRNG } from './rng/SeededRNG';

// Matter.js Common.isElement expects HTMLElement in browser builds.
// Define a stub in Node to avoid "nodeType" crashes during server validation.
if (typeof window === 'undefined' && typeof (globalThis as any).HTMLElement === 'undefined') {
  (globalThis as any).HTMLElement = function HTMLElement() {};
}

export type Vec2 = { x: number; y: number };

export type ArcadeSimInput = {
  thrustActive: boolean;
  /** Expected to be a unit vector (or close). */
  thrustVector: Vec2;
};

export type ArcadeSimEvent =
  | {
      type: 'gameOver';
      tick: number;
      reason: 'collision';
      withCubeId?: number;
      relativeSpeed?: number;
    }
  | {
      /** A high-speed (dramatic) collision that should trigger client-only VFX. */
      type: 'dramaticCollision';
      tick: number;
      withCubeId?: number;
      relativeSpeed?: number;
    };

export type ArcadeSimSnapshot = {
  tick: number;
  timeMs: number;
  rngState: number;
  sheep: { x: number; y: number; angle: number };
  cubes: Array<{ id: number; x: number; y: number; angle: number; dim: number }>;
};

export type ArcadeSimRunResult = {
  /** Total wall-clock time spent executing the simulation loop (debug only). */
  elapsedMs: number;
  /** How many packed input segments were provided. */
  inputSegments: number;
  /** How many steps were actually simulated (may be < inputSteps if stopOnGameOver). */
  simulatedSteps: number;
  /** Index of the first step that produced a gameOver event (if any). */
  gameOverStepIndex: number | null;
  /** All events emitted across the run. */
  events: ArcadeSimEvent[];
  /** Final snapshot (either last simulated step or snapshot-at-gameover). */
  finalSnapshot: ArcadeSimSnapshot;
  /** Optional per-step snapshots, for debugging/replay comparisons. */
  snapshots?: ArcadeSimSnapshot[];
};

/**
 * Packed inputs in chunks of 4 numbers:
 * [x, y, thrustStartStep, thrustEndStep, ...]
 *
 * x/y are expected to be the (already-rounded) thrust direction vector components.
 */
export type ArcadeSimPackedInputs = number[];

type CubeState = {
  id: number;
  dim: number;
  body: any;
};

const FRICTION = 0.8;
const STATIC_FRICTION = 1.3;

const SHEEP_FRICTION_AIR = 0.05;
const SHEEP_RESTITUTION = 0.8;

const CUBE_RESTITUTION = 0.5;
const CUBE_FRICTION_AIR = 0.0;

/**
 * Deterministic arcade simulation:
 * - Owns Matter stepping
 * - Owns RNG consumption
 * - Owns outcome-affecting spawning + game-over rules
 *
 * No rendering imports allowed.
 */
export class ArcadeSim {
  private readonly rng: SeededRNG;
  private readonly engine: any;
  private readonly world: any;

  private readonly sheepBody: any;
  private readonly groundBody: any;

  private timeMs: number = 0;
  private tick: number = 0;
  private eventQueue: ArcadeSimEvent[] = [];
  private didGameOver: boolean = false;
  private pendingSheepImpulses: Array<{ point: Vec2; force: Vec2 }> = [];

  private nextCubeId: number = 1;
  private cubesById: Map<number, CubeState> = new Map();

  // For spawning heuristics (copied from old World.ts behavior)
  private lastSheepX: number = 0;
  private lastSheepY: number = 0;
  private spawnProgress: number = 0;
  private spawnThreshold: number = 1;

  // For thrust/rotation behavior (copied from old Sheep.ts behavior)
  private controlRotationZ: number = 0;
  private lastThrustActive: boolean = false;

  constructor(seedParts: string[]) {
    this.rng = new SeededRNG(seedParts);

    this.engine = matter.Engine.create();
    this.world = this.engine.world;
    this.world.gravity.y = -0.0; // gravity is applied manually per-step for determinism

    // Sheep body shape: copied from old `World.ts` and kept as pure physics config.
    const scale = 0.82;
    const bodyWidth = 105 * scale;
    const bodyHeight = 95 * scale;
    const legWidth = 50 * scale;
    const legHeight = 30 * scale;
    const jetpackWidth = 15 * scale;
    const jetpackHeight = 50 * scale;

    const proxyBody = matter.Body.create();
    const sheepBodyShape = matter.Vertices.create(
      [
        { x: 0, y: 0 }, // a
        { x: bodyWidth, y: 0 }, // b
        { x: bodyWidth, y: -0.1 * jetpackHeight }, // jetpackRight1
        { x: bodyWidth + jetpackWidth, y: -0.1 * jetpackHeight }, // jetpackRight2
        { x: bodyWidth + jetpackWidth, y: -1.1 * jetpackHeight }, // jetpackRight3
        { x: bodyWidth, y: -1.1 * jetpackHeight }, // jetpackRight4
        { x: bodyWidth, y: -bodyHeight }, // c
        { x: bodyWidth / 2 + legWidth / 2, y: -bodyHeight }, // d
        { x: bodyWidth / 2 + legWidth / 2, y: -bodyHeight - legHeight }, // e
        { x: bodyWidth / 2 - legWidth / 2, y: -bodyHeight - legHeight }, // f
        { x: bodyWidth / 2 - legWidth / 2, y: -bodyHeight }, // g
        { x: 0, y: -bodyHeight }, // h
        { x: 0, y: -1.1 * jetpackHeight }, // jetpackLeft1
        { x: 0 - jetpackWidth, y: -1.1 * jetpackHeight }, // jetpackLeft2
        { x: 0 - jetpackWidth, y: -0.1 * jetpackHeight }, // jetpackLeft3
        { x: 0, y: -0.1 * jetpackHeight }, // jetpackLeft4
      ],
      proxyBody
    );
    this.sheepBody = matter.Bodies.fromVertices(0, 0, sheepBodyShape);
    this.sheepBody.friction = FRICTION;
    this.sheepBody.restitution = SHEEP_RESTITUTION;
    this.sheepBody.frictionAir = SHEEP_FRICTION_AIR;
    this.sheepBody.frictionStatic = STATIC_FRICTION;
    matter.Body.setMass(this.sheepBody, 5);
    matter.World.add(this.world, this.sheepBody);

    const groundHeight = 200;
    const groundWidth = 2000;
    this.groundBody = matter.Bodies.rectangle(-groundWidth / 2, 0, groundWidth, groundHeight, {
      restitution: 0.5,
      isStatic: true,
      friction: FRICTION,
      frictionStatic: STATIC_FRICTION,
    });
    matter.Body.setPosition(this.groundBody, { x: 0, y: -370 - groundHeight });
    matter.World.add(this.world, this.groundBody);

    this._attachCollisionHandlers();
    this.resetRun();
  }

  resetRun() {
    // Reset per-run deterministic state (and also reset RNG, matching previous behavior of "deterministic retries").
    this.rng.reset();
    this.timeMs = 0;
    this.tick = 0;
    this.eventQueue = [];
    this.didGameOver = false;
    this.pendingSheepImpulses = [];

    this.controlRotationZ = 0;
    this.lastThrustActive = false;

    // Reset sheep body.
    matter.Body.setPosition(this.sheepBody, { x: 0, y: -410 });
    matter.Body.setAngle(this.sheepBody, 0);
    matter.Body.setVelocity(this.sheepBody, { x: 0, y: 0 });
    matter.Body.setAngularVelocity(this.sheepBody, 0);
    this.lastSheepX = this.sheepBody.position.x;
    this.lastSheepY = this.sheepBody.position.y;
    this._resetSpawnSchedule();

    // Remove all cubes from the world.
    for (const cube of this.cubesById.values()) {
      matter.World.remove(this.world, cube.body);
    }
    this.cubesById.clear();
    this.nextCubeId = 1;
  }

  step(dtMs: number, input: ArcadeSimInput): { events: ArcadeSimEvent[]; snapshot: ArcadeSimSnapshot } {
    this.eventQueue = [];

    // Apply any deferred collision impulse from the previous tick.
    // This mimics the previous client-side `setTimeout(..., 0)` behavior which effectively applied
    // the repulsion on the next physics step.
    if (this.pendingSheepImpulses.length > 0) {
      for (let i = 0; i < this.pendingSheepImpulses.length; i++) {
        const imp = this.pendingSheepImpulses[i];
        matter.Body.applyForce(this.sheepBody, imp.point, imp.force);
      }
      this.pendingSheepImpulses.length = 0;
    }

    // After game-over triggers we still keep physics stepping (so bodies keep moving),
    // but we ignore player inputs and stop spawning new hazards.
    const effectiveInput: ArcadeSimInput = this.didGameOver
      ? { thrustActive: false, thrustVector: { x: 0, y: 1 } }
      : input;

    // 1) Apply per-step forces (gravity + thrust), copied from previous Sheep.physicsStep().
    this._applySheepForces(effectiveInput);

    // 2) Keep ground centered under sheep (copied from previous World.physicsStep()).
    matter.Body.setPosition(this.groundBody, { x: this.sheepBody.position.x, y: this.groundBody.position.y });

    // 3) Step physics.
    matter.Engine.update(this.engine, dtMs);
    this.timeMs += dtMs;
    this.tick += 1;

    // 4) Spawn + cleanup cubes (authoritative).
    this._spawnAndCullCubes({ allowSpawning: this.didGameOver === false });

    return { events: this.eventQueue.slice(), snapshot: this.getSnapshot() };
  }

  getSnapshot(): ArcadeSimSnapshot {
    const cubes: Array<{ id: number; x: number; y: number; angle: number; dim: number }> = [];
    const ids = Array.from(this.cubesById.keys()).sort((a, b) => a - b);
    for (let i = 0; i < ids.length; i++) {
      const cube = this.cubesById.get(ids[i]);
      if (!cube) continue;
      const b = cube.body;
      cubes.push({
        id: cube.id,
        x: b.position.x,
        y: b.position.y,
        angle: b.angle,
        dim: cube.dim,
      });
    }

    return {
      tick: this.tick,
      timeMs: this.timeMs,
      rngState: this.rng.getState(),
      sheep: {
        x: this.sheepBody.position.x,
        y: this.sheepBody.position.y,
        angle: this.sheepBody.angle,
      },
      cubes,
    };
  }

  private _rand() {
    return this.rng.random();
  }

  private _resetSpawnSchedule() {
    this.spawnProgress = 0;
    this.spawnThreshold = this._sampleSpawnThreshold();
  }

  private _sampleSpawnThreshold() {
    // Exponential distribution (mean = 1). This gives step-size independent spawn timing
    // when we accumulate "spawn weight" over time/distance.
    const u = 1 - this._rand();
    return -Math.log(Math.max(1e-12, u));
  }

  private _attachCollisionHandlers() {
    const handleCollision = (event: any) => {
      const pairs = event.pairs;
      for (let p = 0; p < pairs.length; p++) {
        const pair = pairs[p];
        const bodyA = pair.bodyA;
        const bodyB = pair.bodyB;
        if (bodyA !== this.sheepBody && bodyB !== this.sheepBody) {
          continue;
        }

        const otherBody = this.sheepBody === bodyA ? bodyB : bodyA;
        if (otherBody === this.groundBody) {
          continue;
        }

        // Only consider "cubes" as deadly obstacles; ignore any other future bodies.
        const cubeId: number | undefined = otherBody?.plugin?.cubeId;
        if (!cubeId) {
          continue;
        }

        const dx = this.sheepBody.position.x - otherBody.position.x;
        const dy = this.sheepBody.position.y - otherBody.position.y;
        const d = Math.sqrt(dx * dx + dy * dy) || 1;

        const speedDX = this.sheepBody.velocity.x - otherBody.velocity.x;
        const speedDY = this.sheepBody.velocity.y - otherBody.velocity.y;
        const speedD = Math.sqrt(speedDX * speedDX + speedDY * speedDY);

        // Dramatic collision rule (matches legacy behavior): speedD > 10.
        const triggersDramatic = speedD > 10;
        if (triggersDramatic) {
          const repulsionFactor = 0.2;
          // Repulsive force on sheep (outcome-affecting) — deferred to next step to match old behavior.
          const contactPoint = this._getContactPoint(pair) ?? { x: this.sheepBody.position.x, y: this.sheepBody.position.y };
          this.pendingSheepImpulses.push({
            point: contactPoint,
            force: {
              x: repulsionFactor * dx / d,
              y: repulsionFactor * dy / d,
            },
          });

          // Always emit a dramaticCollision event so the client can play VFX,
          // even if we're already in the game-over sequence.
          this.eventQueue.push({
            type: 'dramaticCollision',
            tick: this.tick,
            withCubeId: cubeId,
            relativeSpeed: speedD,
          });

          // Game-over triggers only ONCE, but dramatic collisions keep happening after.
          if (!this.didGameOver) {
            this.didGameOver = true;
            this.eventQueue.push({
              type: 'gameOver',
              tick: this.tick,
              reason: 'collision',
              withCubeId: cubeId,
              relativeSpeed: speedD,
            });
          }
        }
      }
    };

    matter.Events.on(this.engine, 'collisionStart', handleCollision);
    matter.Events.on(this.engine, 'collisionActive', handleCollision);
  }

  private _getContactPoint(pair: any): Vec2 | null {
    // Matter can expose contacts via different shapes depending on build/version.
    // Prefer a stable array-based contact list when available.
    const c0 = (pair && pair.activeContacts && pair.activeContacts[0]) || (pair && pair.contacts && pair.contacts[0]) || null;
    if (c0) {
      const v = (c0.vertex ?? c0);
      if (v && typeof v.x === 'number' && typeof v.y === 'number') {
        return { x: v.x, y: v.y };
      }
    }

    // Fallback: contacts might be an object map; pick the first entry.
    const contacts = pair && pair.contacts;
    if (contacts && typeof contacts === 'object') {
      const keys = Object.keys(contacts);
      if (keys.length > 0) {
        const c = contacts[keys[0]];
        const v = (c && (c.vertex ?? c)) || null;
        if (v && typeof v.x === 'number' && typeof v.y === 'number') {
          return { x: v.x, y: v.y };
        }
      }
    }

    return null;
  }

  private _applySheepForces(input: ArcadeSimInput) {
    const g = 0.02;
    const x = this.sheepBody.position.x;
    const y = this.sheepBody.position.y;

    // gravity
    matter.Body.applyForce(this.sheepBody, { x, y: y - 1 }, { x: 0, y: -g });

    const thrustVector = input.thrustVector || { x: 0, y: 1 };
    const thrustAngle = Math.atan2(thrustVector.y, thrustVector.x);

    if (input.thrustActive === true) {
      const thrustForce = g * 2.2;

      // Reset angular velocity when thrusters are used
      matter.Body.setAngularVelocity(this.sheepBody, 0);

      // Outcome-affecting orientation control (copied from old Sheep.ts)
      this.controlRotationZ = 0.3 * (thrustAngle - Math.PI / 2) + 0.7 * this.controlRotationZ;
      matter.Body.rotate(this.sheepBody, this.controlRotationZ - this.sheepBody.angle);

      matter.Body.applyForce(
        this.sheepBody,
        { x: x - thrustVector.x, y: y - thrustVector.y },
        { x: thrustForce * thrustVector.x, y: thrustForce * thrustVector.y }
      );
    } else if (this.lastThrustActive === true) {
      // Small angular kick when thrust stops (copied behavior).
      matter.Body.setAngularVelocity(this.sheepBody, this.sheepBody.angle * 0.1);
    }

    this.lastThrustActive = input.thrustActive === true;
  }

  private _spawnAndCullCubes(opts?: { allowSpawning?: boolean }) {
    const allowSpawning = opts?.allowSpawning !== false;
    const sheepX = this.sheepBody.position.x;
    const sheepY = this.sheepBody.position.y;
    const sheepDX = sheepX - this.lastSheepX;
    const sheepDY = sheepY - this.lastSheepY;

    const initialSpawnChance = 0.03;
    const spawnChance = initialSpawnChance + (1 - initialSpawnChance) * (1 - 128_000 / (128_000 + this.timeMs));

    const spawnDistance = 1800;
    const maxCubeDim = 500;
    const minCubeDim = 100;

    const travel = Math.sqrt(sheepDX * sheepDX + sheepDY * sheepDY);
    const spawnWeight = spawnChance * 0.05 * travel;
    if (allowSpawning && spawnWeight > 0) {
      this.spawnProgress += spawnWeight;
      while (this.spawnProgress >= this.spawnThreshold) {
        this.spawnProgress -= this.spawnThreshold;
        this.spawnThreshold = this._sampleSpawnThreshold();

        const dim = 30 + minCubeDim * Math.pow(this._rand(), 0.1) + (maxCubeDim - minCubeDim) * Math.pow(this._rand(), 10);

        const body = matter.Bodies.rectangle(-dim / 2, -dim / 2, dim, dim);
        body.friction = FRICTION;
        body.restitution = CUBE_RESTITUTION;
        body.frictionAir = CUBE_FRICTION_AIR;
        body.frictionStatic = STATIC_FRICTION;
        matter.Body.setMass(body, (dim * dim) / 20);

        const spawnAngle = Math.atan2(this.sheepBody.position.y - this.lastSheepY, this.sheepBody.position.x - this.lastSheepX) + (this._rand() - 0.5) * 1.1;
        const x = Math.cos(spawnAngle) * spawnDistance + sheepX;
        const y = Math.sin(spawnAngle) * spawnDistance + sheepY;
        matter.Body.setPosition(body, { x, y });

        // initial force
        const angleSource = -spawnAngle + (this._rand() - 0.5) * 0.4;
        const impulseSource = {
          x: x + Math.cos(angleSource) * 50,
          y: y - Math.sin(angleSource) * 50,
        };

        const forceFactor = 0.5 * (10.0 + (dim * dim) / 500) * Math.pow(this._rand(), 8);
        const angleForce = angleSource + (this._rand() - 0.5) * Math.PI;
        const impulseForce = {
          x: -forceFactor * Math.cos(angleForce),
          y: forceFactor * Math.sin(angleForce),
        };

        matter.Body.applyForce(body, impulseSource, impulseForce);
        matter.Body.setAngle(body, this._rand() * Math.PI * 2);

        const cubeId = this.nextCubeId++;
        body.plugin = { cubeId }; // pure data tag for deterministic identification
        matter.World.add(this.world, body);

        this.cubesById.set(cubeId, { id: cubeId, dim, body });
      }
    }

    // Deterministic culling: based on distance to sheep (NOT camera/view bounds).
    // This avoids outcome depending on rendering/camera state.
    const clearDistance = spawnDistance + 1200;
    const clearDistance2 = clearDistance * clearDistance;
    const toRemove: number[] = [];

    for (const cube of this.cubesById.values()) {
      const b = cube.body;
      const dx = b.position.x - sheepX;
      const dy = b.position.y - sheepY;
      const dist2 = dx * dx + dy * dy;
      if (dist2 <= clearDistance2) {
        continue;
      }

      // Remove only if it's moving away from sheep (avoid popping obstacles that might come back).
      const rvx = b.velocity.x - this.sheepBody.velocity.x;
      const rvy = b.velocity.y - this.sheepBody.velocity.y;
      const movingAway = rvx * dx + rvy * dy > 0;
      if (movingAway) {
        toRemove.push(cube.id);
      }
    }

    for (let i = 0; i < toRemove.length; i++) {
      const id = toRemove[i];
      const cube = this.cubesById.get(id);
      if (!cube) continue;
      matter.World.remove(this.world, cube.body);
      this.cubesById.delete(id);
    }

    this.lastSheepX = sheepX;
    this.lastSheepY = sheepY;
  }

  /**
   * Convenience batch runner for debugging / offline verification.
   *
   * Inputs are provided as a packed array of segments:
   * [x, y, startStep, endStep] repeated.
   *
   * Determinism notes:
   * - The simulation outcome depends ONLY on `seedParts`, `dtMs`, and `inputsPacked`.
   * - Timing (elapsedMs) is measured for debugging and does not affect results.
   */
  static run(
    seedParts: string[],
    inputsPacked: ArcadeSimPackedInputs,
    dtMs: number,
    opts?: {
      stopOnGameOver?: boolean;
      captureSnapshots?: boolean;
      postGameOverMs?: number;
      postGameOverSteps?: number;
      /** Force sim to run at least this many steps (0-based step count). */
      maxSteps?: number;
    }
  ): ArcadeSimRunResult {
    const stopOnGameOver = opts?.stopOnGameOver !== false;
    const captureSnapshots = opts?.captureSnapshots === true;
    const postGameOverStepsFromMs = Number.isFinite(opts?.postGameOverMs)
      ? Math.max(0, Math.ceil((opts?.postGameOverMs as number) / dtMs))
      : 0;
    const postGameOverSteps = Math.max(
      postGameOverStepsFromMs,
      Number.isFinite(opts?.postGameOverSteps) ? Math.max(0, Math.ceil(opts?.postGameOverSteps as number)) : 0
    );

    const sim = new ArcadeSim(seedParts);
    sim.resetRun();

    const events: ArcadeSimEvent[] = [];
    const snapshots: ArcadeSimSnapshot[] | undefined = captureSnapshots ? [] : undefined;
    let gameOverStepIndex: number | null = null;
    let finalSnapshot: ArcadeSimSnapshot = sim.getSnapshot();

    const stride = 4;
    if (inputsPacked.length % stride !== 0) {
      throw new Error(`inputsPacked length must be a multiple of ${stride} (got ${inputsPacked.length})`);
    }
    const segmentCount = inputsPacked.length / stride;

    // Validate packed inputs for deterministic replay parity (client/server).
    for (let s = 0; s < segmentCount; s++) {
      const off = s * stride;
      const x = inputsPacked[off + 0];
      const y = inputsPacked[off + 1];
      const startStep = inputsPacked[off + 2];
      const endStep = inputsPacked[off + 3];

      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        throw new Error(`inputsPacked[${off}] x/y must be finite (got x=${x}, y=${y})`);
      }
      if (!Number.isFinite(startStep) || !Number.isFinite(endStep)) {
        throw new Error(`inputsPacked[${off}] startStep/endStep must be finite (got start=${startStep}, end=${endStep})`);
      }
      if (!Number.isInteger(startStep) || !Number.isInteger(endStep)) {
        throw new Error(`inputsPacked[${off}] startStep/endStep must be integers (got start=${startStep}, end=${endStep})`);
      }
      if (startStep < 0) {
        throw new Error(`inputsPacked[${off}] startStep must be >= 0 (got ${startStep})`);
      }
      if (endStep < startStep) {
        throw new Error(`inputsPacked[${off}] endStep must be >= startStep (got start=${startStep}, end=${endStep})`);
      }
      if (s > 0) {
        const prevEnd = inputsPacked[(s - 1) * stride + 3];
        if (startStep < prevEnd) {
          throw new Error(`inputsPacked segments must be non-overlapping and ordered (segment ${s} starts at ${startStep} but previous ended at ${prevEnd})`);
        }
      }
    }

    // Determine how long to simulate if we don't stop on gameOver.
    let maxEndStep = 0;
    for (let s = 0; s < segmentCount; s++) {
      const endStep = inputsPacked[s * stride + 3];
      if (Number.isFinite(endStep) && endStep > maxEndStep) {
        maxEndStep = endStep;
      }
    }
    let totalSteps = Math.max(0, Math.ceil(maxEndStep));
    if (Number.isFinite(opts?.maxSteps)) {
      totalSteps = Math.max(totalSteps, Math.ceil(opts?.maxSteps as number));
    }

    const now = () => {
      // Prefer performance.now() when available; fall back to Date.now() for Node/older environments.
      const p: any = (typeof performance !== 'undefined' ? performance : null);
      return p && typeof p.now === 'function' ? p.now() : Date.now();
    };
    const start = now();

    let simulatedSteps = 0;
    let segIndex = 0;
    for (let i = 0; i < totalSteps; i++) {
      // Advance past ended segments.
      while (segIndex < segmentCount) {
        const endStep = inputsPacked[segIndex * stride + 3];
        if (i >= endStep) {
          segIndex += 1;
          continue;
        }
        break;
      }

      let stepInput: ArcadeSimInput = { thrustActive: false, thrustVector: { x: 0, y: 1 } };
      if (segIndex < segmentCount) {
        const x = inputsPacked[segIndex * stride + 0];
        const y = inputsPacked[segIndex * stride + 1];
        const startStep = inputsPacked[segIndex * stride + 2];
        const endStep = inputsPacked[segIndex * stride + 3];

        if (i >= startStep && i < endStep) {
          stepInput = { thrustActive: true, thrustVector: { x, y } };
        }
      }

      const res = sim.step(dtMs, stepInput);
      simulatedSteps += 1;

      for (let e = 0; e < res.events.length; e++) {
        const evt = res.events[e];
        events.push(evt);
        if (evt.type === 'gameOver' && gameOverStepIndex === null) {
          gameOverStepIndex = i;
          if (stopOnGameOver && postGameOverSteps > 0) {
            const minTotalSteps = gameOverStepIndex + postGameOverSteps + 1;
            if (totalSteps < minTotalSteps) {
              totalSteps = minTotalSteps;
            }
          }
        }
      }

      finalSnapshot = res.snapshot;
      if (snapshots) {
        snapshots.push(res.snapshot);
      }

      if (stopOnGameOver && gameOverStepIndex !== null) {
        if (postGameOverSteps > 0) {
          if (i >= gameOverStepIndex + postGameOverSteps) {
            break;
          }
          continue;
        }
        break;
      }
    }

    const elapsedMs = now() - start;

    return {
      elapsedMs,
      inputSegments: segmentCount,
      simulatedSteps,
      gameOverStepIndex,
      events,
      finalSnapshot,
      snapshots,
    };
  }
}
